import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sparkles,
  Download,
  Save,
  Trash2,
  Loader2,
  Copy,
  Megaphone,
  Image as ImageIcon,
  FolderOpen,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Quick-start templates so the user can fire off common notices in one click.
const TEMPLATES = [
  {
    id: "early-close",
    label: "Early Close",
    mood: "playful",
    headline: "CLOSING EARLY",
    body:
      "We're closing the shop at 14:00 tomorrow for staff training. Pop in before then to stock up!",
    valid_until: "Tomorrow only",
  },
  {
    id: "public-holiday",
    label: "Public Holiday",
    mood: "festive",
    headline: "HOLIDAY HOURS",
    body: "We will be CLOSED on the upcoming public holiday. Normal trading resumes the next day.",
    valid_until: "Public Holiday",
  },
  {
    id: "weekend-special",
    label: "Weekend Special",
    mood: "sale",
    headline: "WEEKEND SPECIALS!",
    body: "Big savings on selected hardware and farming supplies. While stocks last.",
    valid_until: "This weekend only",
  },
  {
    id: "load-shedding",
    label: "Load Shedding",
    mood: "urgent",
    headline: "LOAD SHEDDING NOTICE",
    body: "Card machines may be offline during load shedding. Please bring cash where possible.",
    valid_until: "Until further notice",
  },
  {
    id: "storm-warning",
    label: "Storm Warning",
    mood: "weather",
    headline: "STORM EXPECTED",
    body: "Severe weather is forecast for our area. Please plan your shopping accordingly and travel safely.",
    valid_until: "Until further notice",
  },
  {
    id: "new-stock",
    label: "New Stock In",
    mood: "playful",
    headline: "FRESH STOCK ARRIVED!",
    body: "Come and see what's new in store this week.",
    valid_until: "While stocks last",
  },
];

export default function StoreNotice() {
  // Form state
  const [headline, setHeadline] = useState("CLOSING EARLY TOMORROW");
  const [body, setBody] = useState(
    "We're closing at 14:00 for staff training. Pop in before then to stock up!"
  );
  const [mood, setMood] = useState("playful");
  const [moodOptions, setMoodOptions] = useState([]);
  const [language, setLanguage] = useState("english");
  const [languageOptions, setLanguageOptions] = useState([]);
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [companyName, setCompanyName] = useState("BOSVELD BOU & HARDEWARE");
  const [contactInfo, setContactInfo] = useState("");
  const [validUntil, setValidUntil] = useState("Tomorrow only");
  const [format, setFormat] = useState("square");

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [image, setImage] = useState(null); // data URL once generated
  const [imageMeta, setImageMeta] = useState({ mood: null });

  // Saved gallery
  const [savedNotices, setSavedNotices] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const imageRef = useRef(null);

  // --- Bootstrap: load moods + saved notices in parallel ---
  useEffect(() => {
    const load = async () => {
      try {
        const [moodsRes, languagesRes, savedRes, settingsRes] = await Promise.allSettled([
          axios.get(`${API}/api/store-notice/moods`, { timeout: 10000 }),
          axios.get(`${API}/api/store-notice/languages`, { timeout: 10000 }),
          axios.get(`${API}/api/store-notice/saved`, { timeout: 10000 }),
          axios.get(`${API}/api/settings/company`, { timeout: 10000 }),
        ]);

        if (moodsRes.status === "fulfilled") {
          setMoodOptions(moodsRes.value.data || []);
        }
        if (languagesRes.status === "fulfilled") {
          setLanguageOptions(languagesRes.value.data || []);
        }
        if (savedRes.status === "fulfilled") {
          setSavedNotices(savedRes.value.data || []);
        }
        if (settingsRes.status === "fulfilled" && settingsRes.value.data) {
          const s = settingsRes.value.data;
          if (s.name) setCompanyName(s.name);
          // Build a default contact line if available
          const contactBits = [];
          if (s.phone) contactBits.push(`Tel: ${s.phone}`);
          if (s.email) contactBits.push(s.email);
          if (contactBits.length) setContactInfo(contactBits.join(" • "));
        }
      } catch (err) {
        // Soft fail — defaults are good enough.
        console.warn("Bootstrap fetch failed", err);
      }
    };
    load();
  }, []);

  const reloadSaved = async () => {
    setLoadingSaved(true);
    try {
      const res = await axios.get(`${API}/api/store-notice/saved`);
      setSavedNotices(res.data || []);
    } catch {
      // ignore
    } finally {
      setLoadingSaved(false);
    }
  };

  const applyTemplate = (tpl) => {
    setHeadline(tpl.headline);
    setBody(tpl.body);
    setMood(tpl.mood);
    setValidUntil(tpl.valid_until || "");
    setImage(null);
    toast.success(`Loaded template: ${tpl.label}`);
  };

  const handleGenerate = async () => {
    if (!headline.trim()) {
      toast.error("Please enter a headline");
      return;
    }

    setGenerating(true);
    const toastId = toast.loading(
      language !== "english" && autoTranslate
        ? "Translating + generating with AI… can take 20-40s"
        : "Generating notice with AI… this can take 10-30s"
    );
    try {
      const res = await axios.post(
        `${API}/api/store-notice/generate`,
        {
          headline,
          body,
          mood,
          language,
          auto_translate: autoTranslate,
          company_name: companyName,
          contact_info: contactInfo,
          valid_until: validUntil,
          format,
        },
        { timeout: 120000 }
      );

      if (res.data?.image_base64) {
        const mime = res.data.mime_type || "image/png";
        setImage(`data:${mime};base64,${res.data.image_base64}`);
        setImageMeta({ mood: res.data.mood, language: res.data.language, translated: res.data.translated });
        toast.dismiss(toastId);
        const langLabel = languageOptions.find((l) => l.id === language)?.label || language;
        toast.success(
          language !== "english"
            ? `Notice generated in ${langLabel}! Scroll down to preview.`
            : "Notice generated! Scroll down to preview."
        );
      } else {
        toast.dismiss(toastId);
        toast.error("AI did not return an image. Please try again.");
      }
    } catch (err) {
      toast.dismiss(toastId);
      const detail = err.response?.data?.detail || err.message || "Generation failed";
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!image) return;
    const link = document.createElement("a");
    link.href = image;
    link.download = `store-notice-${(mood || "notice").toLowerCase()}-${Date.now()}.png`;
    link.click();
    toast.success("Downloaded!");
  };

  const handleCopyImage = async () => {
    if (!image) return;
    try {
      // Convert data URL to Blob and use Clipboard API
      const resp = await fetch(image);
      const blob = await resp.blob();
      // Clipboard write of images only supports PNG reliably
      const item = new ClipboardItem({ [blob.type]: blob });
      await navigator.clipboard.write([item]);
      toast.success("Image copied — paste into WhatsApp / Facebook");
    } catch (err) {
      toast.error(
        "Couldn't copy automatically. Click Download and attach the PNG instead."
      );
    }
  };

  const handleSave = async () => {
    if (!image) return;
    setSaving(true);
    try {
      // Strip data: prefix
      const b64 = image.split(",")[1] || image;
      await axios.post(`${API}/api/store-notice/save`, {
        headline,
        body,
        mood,
        language,
        valid_until: validUntil,
        company_name: companyName,
        contact_info: contactInfo,
        format,
        image_base64: b64,
      });
      toast.success("Notice saved");
      reloadSaved();
    } catch {
      toast.error("Failed to save notice");
    } finally {
      setSaving(false);
    }
  };

  const handleLoadSaved = async (noticeId) => {
    try {
      const res = await axios.get(`${API}/api/store-notice/saved/${noticeId}`);
      const n = res.data;
      setHeadline(n.headline || "");
      setBody(n.body || "");
      setMood(n.mood || "playful");
      setLanguage(n.language || "english");
      setValidUntil(n.valid_until || "");
      setCompanyName(n.company_name || companyName);
      setContactInfo(n.contact_info || "");
      setFormat(n.format || "square");
      if (n.image_base64) {
        setImage(`data:image/png;base64,${n.image_base64}`);
        setImageMeta({ mood: n.mood });
      } else {
        setImage(null);
      }
      toast.success("Loaded saved notice");
      // Scroll to top so user sees the form filled in
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Failed to load notice");
    }
  };

  const handleDeleteSaved = async (noticeId) => {
    if (!window.confirm("Delete this saved notice?")) return;
    try {
      await axios.delete(`${API}/api/store-notice/saved/${noticeId}`);
      toast.success("Deleted");
      reloadSaved();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleReset = () => {
    setImage(null);
    setImageMeta({ mood: null });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="store-notice-page">
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-emerald-500 to-amber-400 flex items-center justify-center text-white">
              <Megaphone className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Store Notice Generator</h1>
              <p className="text-xs text-gray-500">
                Make a quick branded poster for WhatsApp, Facebook or in-store screens.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* === Left column: form === */}
        <div className="lg:col-span-2 space-y-4">
          {/* Quick-start templates */}
          <Card data-testid="notice-templates-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                Quick-start templates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {TEMPLATES.map((tpl) => (
                  <Button
                    key={tpl.id}
                    variant="outline"
                    size="sm"
                    data-testid={`tpl-${tpl.id}`}
                    onClick={() => applyTemplate(tpl)}
                  >
                    {tpl.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">What&apos;s the notice about?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="notice-headline" className="text-xs">
                  Headline <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="notice-headline"
                  data-testid="notice-headline-input"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="e.g. CLOSING EARLY TOMORROW"
                  maxLength={200}
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Keep it punchy — this is the BIG text on the poster.
                </p>
              </div>

              <div>
                <Label htmlFor="notice-body" className="text-xs">
                  Message body
                </Label>
                <Textarea
                  id="notice-body"
                  data-testid="notice-body-input"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={14}
                  placeholder={`Paste a longer notice here — line breaks and lists are preserved.\n\nFormatting tips:\n  **bold text**  → renders bold\n  # Big Heading   → renders larger\n  1) First point   → renders as a list`}
                  maxLength={4000}
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Use <code>**bold**</code>, <code>#</code> headings, blank lines and numbered lists — the poster keeps the layout.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Mood / Theme</Label>
                  <Select value={mood} onValueChange={setMood}>
                    <SelectTrigger data-testid="notice-mood-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {moodOptions.length === 0 ? (
                        <SelectItem value="playful">Playful / Fun</SelectItem>
                      ) : (
                        moodOptions.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.label}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-gray-400 mt-1">
                    Change the mood and re-generate for a different vibe.
                  </p>
                </div>

                <div>
                  <Label className="text-xs">Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger data-testid="notice-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languageOptions.length === 0 ? (
                        <SelectItem value="english">English</SelectItem>
                      ) : (
                        languageOptions.map((l) => (
                          <SelectItem key={l.id} value={l.id}>
                            {l.label}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {language !== "english" && (
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoTranslate}
                        onChange={(e) => setAutoTranslate(e.target.checked)}
                        data-testid="notice-autotranslate-toggle"
                        className="rounded border-gray-300"
                      />
                      <span className="text-[11px] text-gray-600">
                        Auto-translate my English text first
                      </span>
                    </label>
                  )}
                  {language === "english" && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      Pick another language to translate &amp; render the poster in.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-xs">Format</Label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger data-testid="notice-format-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="square">Square 1:1 (WhatsApp / Facebook)</SelectItem>
                    <SelectItem value="portrait">Portrait 4:5 (Status / Stories)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Validity / Date note</Label>
                  <Input
                    data-testid="notice-validity-input"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                    placeholder="e.g. Friday 28 Feb only"
                  />
                </div>
                <div>
                  <Label className="text-xs">Contact strip (optional)</Label>
                  <Input
                    data-testid="notice-contact-input"
                    value={contactInfo}
                    onChange={(e) => setContactInfo(e.target.value)}
                    placeholder="e.g. Tel: 014 555 1234"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Footer brand</Label>
                <Input
                  data-testid="notice-company-input"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>

              <div className="pt-2 flex flex-wrap gap-2">
                <Button
                  onClick={handleGenerate}
                  disabled={generating || !headline.trim()}
                  data-testid="notice-generate-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Notice
                    </>
                  )}
                </Button>
                {image && (
                  <Button variant="outline" onClick={handleReset} data-testid="notice-clear-btn">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Clear Preview
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          {image && (
            <Card data-testid="notice-preview-card">
              <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-emerald-600" />
                  Preview {imageMeta.mood ? `· ${imageMeta.mood}` : ""}
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopyImage}
                    data-testid="notice-copy-btn"
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDownload}
                    data-testid="notice-download-btn"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="notice-save-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex justify-center bg-gray-100 rounded-lg p-3">
                  <img
                    ref={imageRef}
                    src={image}
                    alt="Generated store notice"
                    data-testid="notice-preview-image"
                    className="max-h-[640px] rounded-lg shadow-md"
                  />
                </div>
                {imageMeta.translated && Object.keys(imageMeta.translated).length > 0 && (
                  <div
                    className="mt-3 text-[11px] bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-900"
                    data-testid="notice-translated-preview"
                  >
                    <div className="font-semibold mb-1">
                      Translated to {languageOptions.find((l) => l.id === imageMeta.language)?.label || imageMeta.language}:
                    </div>
                    {imageMeta.translated.headline && (
                      <div><strong>Headline:</strong> {imageMeta.translated.headline}</div>
                    )}
                    {imageMeta.translated.body && (
                      <div><strong>Body:</strong> {imageMeta.translated.body}</div>
                    )}
                    {imageMeta.translated.valid_until && (
                      <div><strong>Validity:</strong> {imageMeta.translated.valid_until}</div>
                    )}
                    <div className="text-[10px] text-amber-700 mt-1 italic">
                      Tip: AI translation can drift. Double-check before sending to customers.
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-gray-500 mt-3 text-center">
                  Tip: Not happy with the result? Change the <strong>mood</strong> or tweak the
                  wording and click <strong>Generate Notice</strong> again.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* === Right column: saved gallery === */}
        <div className="space-y-4">
          <Card data-testid="saved-notices-card">
            <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-amber-500" />
                Saved notices
              </CardTitle>
              {loadingSaved && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
            </CardHeader>
            <CardContent>
              {savedNotices.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-6">
                  No saved notices yet. Generate one and click <strong>Save</strong> to keep it.
                </p>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {savedNotices.map((n) => (
                    <div
                      key={n.id}
                      className="border rounded-lg p-2 hover:bg-gray-50 cursor-pointer flex items-start justify-between gap-2"
                      data-testid={`saved-notice-${n.id}`}
                    >
                      <div
                        className="flex-1 min-w-0"
                        onClick={() => handleLoadSaved(n.id)}
                      >
                        <div className="text-xs font-semibold text-gray-900 truncate">
                          {n.headline || "Untitled"}
                        </div>
                        <div className="text-[10px] text-gray-500 truncate">
                          {n.mood ? `${n.mood} · ` : ""}
                          {n.created_at
                            ? new Date(n.created_at).toLocaleString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : ""}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSaved(n.id);
                        }}
                        data-testid={`delete-notice-${n.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="pt-5">
              <p className="text-xs text-emerald-900 leading-relaxed">
                <strong>How it works:</strong> Pick a quick-start template (or write your own),
                choose a <em>mood</em>, and click <strong>Generate Notice</strong>. The AI builds a
                branded poster you can copy straight into WhatsApp or download as a PNG.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
